import { useState } from 'react';
import { BanIcon, PlusIcon, XIcon } from '@heroicons/react/outline';
import { Toggle } from 'components/Toggle';
import {
  ReturnCustomerRestrictions,
  ReturnBlockedCustomer,
  normalizeReturnCustomerTags,
} from 'types/returnCustomerRestrictions';
import { CustomerRestrictionIdentities } from './CustomerRestrictionIdentities';

type Props = {
  value: ReturnCustomerRestrictions;
  onChange: (value: ReturnCustomerRestrictions) => void;
  loaded: boolean;
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
  onRetry: () => void;
  onLookupOrder: (orderNumber: string) => Promise<ReturnBlockedCustomer>;
};

export const CustomerRestrictionsPolicy = (props: Props) => (
  <CustomerRestrictionsForm key={props.value.storeId} {...props} />
);

const CustomerRestrictionsForm = ({
  value,
  onChange,
  loaded,
  loading,
  saving,
  error,
  onRetry,
  onLookupOrder,
}: Props) => {
  const [draft, setDraft] = useState('');
  const [validationError, setValidationError] = useState('');
  const disabled = !loaded || saving;
  const addTag = () => {
    const tag = draft.trim();
    if (!tag || disabled || !value.enabled) return;
    if (tag.length > 255) {
      setValidationError('Use 255 characters or fewer per tag.');
      return;
    }
    if (
      value.customerTags.some(
        (existingTag) => existingTag.trim().toLowerCase() === tag.toLowerCase(),
      )
    ) {
      setValidationError('This tag is already added.');
      return;
    }
    const customerTags = normalizeReturnCustomerTags([
      ...value.customerTags,
      tag,
    ]);
    if (customerTags.length > 100) {
      setValidationError('You can add up to 100 customer tags.');
      return;
    }
    onChange({ ...value, customerTags });
    setDraft('');
    setValidationError('');
  };

  return (
    <section
      id="customer-restrictions"
      aria-labelledby="customer-restrictions-title"
      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
    >
      <div className="flex items-start justify-between gap-4 px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-lg bg-gray-100 p-2 text-gray-600">
            <BanIcon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h3
              id="customer-restrictions-title"
              className="text-base font-semibold text-gray-900"
            >
              Customer restrictions
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Prevent selected customers from submitting new returns and exchanges.
            </p>
          </div>
        </div>
        <span
          className={`mt-1 rounded-full px-2.5 py-1 text-xs font-medium ${
            loaded && value.enabled
              ? 'bg-amber-50 text-amber-800'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {!loaded ? 'Unavailable' : value.enabled ? 'Enabled' : 'Off'}
        </span>
      </div>
      <div className="space-y-5 border-t border-gray-100 p-5">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Block customers from returns and exchanges
            </p>
            <p className="mt-1 max-w-xl text-sm text-gray-500">
              A customer matching any tag, email, or selected customer below
              cannot start a return or exchange.
            </p>
          </div>
          <Toggle
            isEnabled={value.enabled}
            onChange={(enabled) => onChange({ ...value, enabled })}
            disabled={disabled}
            ariaLabel="Block customers from returns and exchanges"
          />
        </div>
        {loading && (
          <p role="status" className="text-sm text-gray-500">
            Loading customer restrictions…
          </p>
        )}
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            <p>{error}</p>
            {!loaded && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-2 font-semibold underline"
              >
                Retry loading restrictions
              </button>
            )}
          </div>
        )}
        <fieldset
          disabled={disabled || !value.enabled}
          className={!value.enabled ? 'opacity-60' : undefined}
        >
          <label
            htmlFor="blocked-customer-tag"
            className="mb-2 block text-sm font-medium text-gray-900"
          >
            Blocked Shopify customer tags
          </label>
          <div className="flex gap-2">
            <input
              id="blocked-customer-tag"
              type="text"
              value={draft}
              placeholder="e.g. no-returns"
              aria-describedby="customer-tag-help"
              onChange={(event) => {
                setDraft(event.target.value);
                setValidationError('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTag();
                }
              }}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
            />
            <button
              type="button"
              onClick={addTag}
              disabled={!draft.trim() || disabled || !value.enabled}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Add tag
            </button>
          </div>
          {validationError && (
            <p role="alert" className="mt-2 text-sm text-red-700">
              {validationError}
            </p>
          )}
          {value.customerTags.length > 0 && (
            <ul
              aria-label="Blocked customer tags"
              className="mt-3 flex flex-wrap gap-2"
            >
              {value.customerTags.map((tag) => (
                <li
                  key={tag.toLowerCase()}
                  className="inline-flex max-w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-100 py-1.5 pl-2.5 pr-1.5 text-sm text-gray-800"
                >
                  <span className="break-all">{tag}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${tag}`}
                    onClick={() =>
                      onChange({
                        ...value,
                        customerTags: value.customerTags.filter(
                          (item) => item !== tag,
                        ),
                      })
                    }
                    className="rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                  >
                    <XIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p
            id="customer-tag-help"
            className="mt-2 text-xs leading-5 text-gray-500"
          >
            Use tags from the customer&apos;s Shopify profile. Tags match
            exactly, ignoring capitalization. Add or remove customer tags in
            Shopify.
          </p>
        </fieldset>
        <CustomerRestrictionIdentities
          value={value}
          onChange={onChange}
          onLookupOrder={onLookupOrder}
          disabled={Boolean(disabled || !value.enabled)}
        />
        {loaded &&
          value.enabled &&
          value.customerTags.length + value.blockedEmails.length + value.blockedCustomers.length === 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Add a customer tag, email, or customer from an order to start blocking return and exchange requests.
            </p>
          )}
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Message shown to blocked customers
          </p>
          <p className="mt-1.5 text-sm text-gray-700">
            Online returns and exchanges aren’t available for this order. Contact the
            store for help.
          </p>
        </div>
        <p className="text-xs leading-5 text-gray-500">
          Applies to new return and exchange requests. Existing requests and delivery claims
          remain accessible.
        </p>
      </div>
    </section>
  );
};
